/**
 * Google Drive → Clipboard import (Tier 1: Google Picker + client-side pull).
 *
 * Uses the `drive.file` scope, so we only ever touch files the user explicitly
 * selects in the Picker — this avoids Google's restricted-scope security
 * assessment that `drive.readonly` would require. Selected PDFs (and Google
 * Docs, exported to PDF) are downloaded in the browser and handed to the same
 * upload/index pipeline as a normal upload.
 *
 * Folder limitation (intentional): with `drive.file`, picking a folder grants
 * access to the folder object only — NOT its children — so folder-recursive
 * import is impossible without upgrading to the restricted `drive.readonly`
 * scope. Folders are therefore navigation-only in the Picker; users open a
 * folder and multi-select the files inside (Shift-click / Ctrl-A).
 *
 * Config: this reuses the existing Paperclip Google OAuth setup. The only
 * value that must be supplied is the OAuth client ID (the same web client used
 * for Paperclip login — it just isn't exposed in the frontend Firebase config).
 * The Picker developer key and project number are derived from the Firebase
 * config the paperclip build already has, since they live in the same GCP
 * project:
 *   VITE_GOOGLE_CLIENT_ID   OAuth 2.0 Web client ID (required — gates the feature)
 *   VITE_GOOGLE_API_KEY     optional override; defaults to VITE_FIREBASE_API_KEY
 *   VITE_GOOGLE_APP_ID      optional override; defaults to the project number
 *                           (VITE_FIREBASE_MESSAGING_SENDER_ID)
 */

const GIS_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SRC = "https://apis.google.com/js/api.js";
const SCOPE = "https://www.googleapis.com/auth/drive.file";
const PDF_MIME = "application/pdf";
const GDOC_MIME = "application/vnd.google-apps.document";
const FOLDER_MIME = "application/vnd.google-apps.folder";

export const driveConfig = {
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined,
  // Reuse the existing Paperclip (Firebase) project credentials — both the
  // browser API key and project number belong to the same GCP project. The
  // dedicated overrides exist only for the edge case where the Firebase key is
  // API-restricted and can't call the Picker API.
  apiKey: (import.meta.env.VITE_GOOGLE_API_KEY ||
    import.meta.env.VITE_FIREBASE_API_KEY) as string | undefined,
  appId: (import.meta.env.VITE_GOOGLE_APP_ID ||
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID) as string | undefined,
};

export const isDriveConfigured = (): boolean =>
  !!(driveConfig.clientId && driveConfig.apiKey && driveConfig.appId);

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: any;
    gapi?: any;
  }
}

const loadedScripts = new Set<string>();
function loadScript(src: string): Promise<void> {
  if (loadedScripts.has(src)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => { loadedScripts.add(src); resolve(); };
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
}

let pickerReady = false;
async function ensurePicker(): Promise<void> {
  await loadScript(GAPI_SRC);
  if (pickerReady) return;
  await new Promise<void>((resolve) => window.gapi.load("picker", () => resolve()));
  pickerReady = true;
}

async function getAccessToken(): Promise<string> {
  await loadScript(GIS_SRC);
  return new Promise<string>((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: driveConfig.clientId,
      scope: SCOPE,
      callback: (resp: any) => {
        if (resp?.access_token) resolve(resp.access_token);
        else reject(new Error("Google authorization was cancelled"));
      },
      error_callback: () => reject(new Error("Google authorization failed")),
    });
    client.requestAccessToken();
  });
}

interface PickedItem { id: string; name: string; mimeType: string }

async function openPicker(token: string): Promise<PickedItem[]> {
  await ensurePicker();
  const g = window.google;
  return new Promise<PickedItem[]>((resolve) => {
    // PDFs + Google Docs, with folder navigation + multi-select. Folders are
    // navigation-only: drive.file never grants access to a picked folder's
    // children, so offering "Select" on folders would only produce empty
    // imports. Users open a folder and multi-select files inside instead.
    const docsView = new g.picker.DocsView(g.picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes(`${PDF_MIME},${GDOC_MIME},${FOLDER_MIME}`);
    const picker = new g.picker.PickerBuilder()
      .enableFeature(g.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(driveConfig.apiKey)
      .setAppId(driveConfig.appId)
      .setOAuthToken(token)
      .addView(docsView)
      .setCallback((data: any) => {
        if (data.action === g.picker.Action.PICKED) {
          resolve(
            (data.docs || []).map((d: any) => ({
              id: d.id,
              name: d.name,
              mimeType: d.mimeType,
            })),
          );
        } else if (data.action === g.picker.Action.CANCEL) {
          resolve([]);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

const authHeader = (token: string) => ({ Authorization: `Bearer ${token}` });

async function downloadAsFile(item: PickedItem, token: string): Promise<File | null> {
  try {
    let url: string;
    let filename = item.name;
    if (item.mimeType === GDOC_MIME) {
      url = `https://www.googleapis.com/drive/v3/files/${item.id}/export?mimeType=${PDF_MIME}`;
      if (!filename.toLowerCase().endsWith(".pdf")) filename += ".pdf";
    } else {
      url = `https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`;
    }
    const resp = await fetch(url, { headers: authHeader(token) });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new File([blob], filename, { type: PDF_MIME });
  } catch {
    return null;
  }
}

/**
 * Full flow: authorize → Picker → download the selected files into File
 * objects ready for the existing upload pipeline.
 */
export async function importFromGoogleDrive(): Promise<File[]> {
  const token = await getAccessToken();
  const picked = await openPicker(token);
  const files: File[] = [];
  for (const item of picked) {
    const f = await downloadAsFile(item, token);
    if (f) files.push(f);
  }
  return files;
}
